import { ScopeOfPracticeEnum } from '@umss/shared/utils';
import { FacilityBillableRateDto } from '../../models/facility-billable-rate/facility-billable-rate.dto';
import {
  BillableRateCalculatorData,
  ShiftRateCalculation,
  ScheduledRateInfo,
  ScheduledDifferentialInfo
} from '../../models/facility-billable-rate/billable-rate-calculator.model';

/**
 * Builds a structured format from facility billable rates grouped by ScopeOfPractice
 * for efficient shift rate calculations. All necessary data is already in FacilityBillableRateDto.
 */
export function buildBillableRateCalculatorData(
  facilityRates: FacilityBillableRateDto[]
): BillableRateCalculatorData {
  const result: Partial<BillableRateCalculatorData> = {};

  // Process each facility rate
  for (const rate of facilityRates) {
    const scopeOfPractice = rate.scopeOfPractice;

    result[scopeOfPractice] = {
      normalRate: rate.hourlyRate,
      normalDiff: rate.differentialRate || 0,
      scheduledRates: (rate.scheduledRates || []).map((sr: any) => ({
        rate: sr.rate,
        start: new Date(sr.effectiveFrom),
        end: new Date(sr.effectiveTo)
      })),
      scheduledDiffs: (rate.scheduledDifferentialRates || []).map(
        (sd: any) => ({
          differentialRate: sd.differentialRate,
          start: new Date(sd.effectiveFrom),
          end: new Date(sd.effectiveTo)
        })
      )
    };
  }

  return result as BillableRateCalculatorData;
}

/**
 * Checks if a date falls within a given range (inclusive)
 */
function isDateInRange(date: Date, start: Date, end: Date): boolean {
  return date >= start && date <= end;
}

/**
 * Calculates the shift rate for a given date and scope of practice.
 * Uses short-circuit evaluation (fallback):
 * - BaseRate: ScheduledRate active on date ?? NormalRate
 * - DifferentialRate: ScheduledDiff active on date ?? NormalDiff
 */
export function calculateShiftRate(
  shiftDate: Date,
  scopeOfPractice: ScopeOfPracticeEnum,
  calculatorData: BillableRateCalculatorData
): ShiftRateCalculation {
  const scopeInfo = calculatorData[scopeOfPractice];

  if (!scopeInfo) {
    return {
      date: shiftDate,
      scopeOfPractice,
      baseRate: 0,
      differentialRate: 0,
      totalRate: 0,
      isBaseRateScheduled: false,
      isDifferentialScheduled: false
    };
  }

  // Find active scheduled rate for this date, fallback to normal rate
  const activeScheduledRate = scopeInfo.scheduledRates.find(
    (sr: ScheduledRateInfo) => isDateInRange(shiftDate, sr.start, sr.end)
  );
  const baseRate = activeScheduledRate?.rate ?? scopeInfo.normalRate;
  const isBaseRateScheduled = !!activeScheduledRate;

  // Find active scheduled differential for this date, fallback to normal differential
  const activeScheduledDiff = scopeInfo.scheduledDiffs.find(
    (sd: ScheduledDifferentialInfo) =>
      isDateInRange(shiftDate, sd.start, sd.end)
  );
  const differentialRate =
    activeScheduledDiff?.differentialRate ?? scopeInfo.normalDiff;
  const isDifferentialScheduled = !!activeScheduledDiff;

  return {
    date: shiftDate,
    scopeOfPractice,
    baseRate,
    differentialRate,
    totalRate: baseRate + differentialRate,
    isBaseRateScheduled,
    isDifferentialScheduled
  };
}

/**
 * Gets all unique periods where rates change for a given scope.
 * Useful for rendering timeline or period-based displays.
 */
export function getActivePeriods(
  scopeOfPractice: ScopeOfPracticeEnum,
  calculatorData: BillableRateCalculatorData,
  now: Date = new Date()
): Array<{
  periodStart: Date;
  periodEnd: Date;
  rate: ShiftRateCalculation;
}> {
  const scopeInfo = calculatorData[scopeOfPractice];

  if (!scopeInfo) {
    return [];
  }

  // Collect all important dates
  const importantDates = new Set<number>();
  importantDates.add(now.getTime());

  // Add scheduled rate boundaries
  scopeInfo.scheduledRates.forEach((sr: ScheduledRateInfo) => {
    importantDates.add(sr.start.getTime());
    importantDates.add(sr.end.getTime());
  });

  // Add scheduled differential boundaries
  scopeInfo.scheduledDiffs.forEach((sd: ScheduledDifferentialInfo) => {
    importantDates.add(sd.start.getTime());
    importantDates.add(sd.end.getTime());
  });

  // Sort dates and create periods
  const sortedDates = Array.from(importantDates)
    .sort((a, b) => a - b)
    .map((t) => new Date(t));

  const periods: Array<{
    periodStart: Date;
    periodEnd: Date;
    rate: ShiftRateCalculation;
  }> = [];

  for (let i = 0; i < sortedDates.length - 1; i++) {
    const periodStart = sortedDates[i];
    const periodEnd = sortedDates[i + 1];
    const periodMidpoint = new Date(
      (periodStart.getTime() + periodEnd.getTime()) / 2
    );

    const rate = calculateShiftRate(
      periodMidpoint,
      scopeOfPractice,
      calculatorData
    );

    periods.push({
      periodStart,
      periodEnd,
      rate
    });
  }

  return periods;
}
